import { useEffect, useRef } from 'react';
import './HelpTip.css';

/**
 * Small "?" button that toggles a contextual help popover next to an action.
 *
 * It is CONTROLLED (isOpen / onToggle) on purpose: besides the user clicking
 * the button, the parent can open it at a teachable moment — e.g. ShopDetail
 * opens it right after the first search to explain that the search area can be
 * moved and searched again.
 */
export const HelpTip = ({
  isOpen,
  onToggle,
  title,
  children,
  dismissText = 'Got it',
  align = 'right', // popover side relative to the button: 'right' | 'left'
}) => {
  const wrapRef = useRef(null);

  // Close on outside click / Escape so it never traps the user.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onToggle(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onToggle(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onToggle]);

  return (
    <div className="wrap-helptip" ref={wrapRef}>
      <button
        type="button"
        className={`btn-helptip ${isOpen ? 'active-helptip' : ''}`}
        onClick={() => onToggle(!isOpen)}
        aria-expanded={isOpen}
        aria-label={title}
        title={title}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>

      {isOpen && (
        <div className={`popover-helptip ${align}-helptip`} role="dialog" aria-label={title}>
          <div className="popover-header-helptip">
            <span className="popover-title-helptip">{title}</span>
            <button
              type="button"
              className="popover-close-helptip"
              onClick={() => onToggle(false)}
              aria-label="Close help"
            >
              ×
            </button>
          </div>
          <div className="popover-body-helptip">{children}</div>
          <button
            type="button"
            className="popover-dismiss-helptip"
            onClick={() => onToggle(false)}
          >
            {dismissText}
          </button>
        </div>
      )}
    </div>
  );
};

export default HelpTip;
